import { NonEmpty } from "../components/MethodDiscovery/NonEmpty";

// A data type that indicates if it has had a failure in it's computation at somepoint,
// but has carried on.
// This is somewhat similar to Haskell's Either type, but rather than returning the
// first Left, continue computing and return the final result as a Left.
// i.e. All Ok, or one Recovered.

export type Recoverable<a> = Recovered<a> | Ok<a>;

export type SRecoverable<a> =
  | { readonly type: "recovered"; readonly recovered: a }
  | { readonly type: "ok"; readonly ok: a };

export const serialiseRecoverable = <a>(r: Recoverable<a>): SRecoverable<a> =>
  r.recover<SRecoverable<a>>(
    a => ({ type: "recovered", recovered: a }),
    a => ({ type: "ok", ok: a })
  );
export const deserialiseRecoverable = <a>(r: SRecoverable<a>): Recoverable<a> =>
  r.type === "ok" ? ok(r.ok) : recovered(r.recovered);

// With typescript I cannot write all of the functions on the classes that I want.
// An example of this is an `ap` where the host instance is infered to have an `a`
// of `a => b`. I have writen this as a function that takes both Recoverables needed.
// The down side of this is that either you have "nice" chaining off the classes, but
// the order is revered from what is usually expected. The top level functions give
// the normal order, but you are stuck with ever increasing parenthesis.
//
// This can be partially worked around by doing something like
//
// const r : Recoverable<a>
// const s : Recoverable<b>
// const t : Recoverable<c>
// const f : (a:a) => (b:b) => (c:c) => d
// r.map(f).bind(s.map).bind(r.map) : Recoverable<d>

interface IRecoverable<a> {
  // Functor
  map: <b>(f: (_: a) => b) => Recoverable<b>;
  // Applicative
  // This is a flipped version of Haskell's `ap` and `<*>`, as
  // typescript doesn't let me write the version for when `a`
  // is a function.
  ap: <b>(f: Recoverable<(_: a) => b>) => Recoverable<b>;
  // Monad
  bind: <b>(f: (_: a) => Recoverable<b>) => Recoverable<b>;
  // Foldable
  fold1: <b>(f: (_: a) => b) => b;
  foldr: <b>(f: (z: b, a: a) => b, z: b) => b;
  foldl: <b>(f: (a: a, z: b) => b, z: b) => b;
  // case analysis
  recover: <b>(recovered: (a: a) => b, ok: (a: a) => b) => b;
}

export class Recovered<a> implements IRecoverable<a> {
  private readonly recovered: a;
  constructor(a: a) {
    this.recovered = a;
  }
  map<b>(f: (_: a) => b): Recoverable<b> {
    return recovered(f(this.recovered));
  }
  ap<b>(f: Recoverable<(_: a) => b>): Recoverable<b> {
    return f.map(f => f(this.recovered)).recover(recovered, recovered);
  }
  bind<b>(f: (_: a) => Recoverable<b>): Recoverable<b> {
    return f(this.recovered).recover(recovered, recovered);
  }
  fold1<b>(f: (a: a) => b): b {
    return f(this.recovered);
  }
  foldr<b>(f: (z: b, a: a) => b, z: b): b {
    return f(z, this.recovered);
  }
  foldl<b>(f: (a: a, z: b) => b, z: b): b {
    return f(this.recovered, z);
  }
  recover<b>(recovered: (a: a) => b, ok: (a: a) => b): b {
    return recovered(this.recovered);
  }
}

export class Ok<a> implements IRecoverable<a> {
  private readonly ok: a;
  constructor(a: a) {
    this.ok = a;
  }
  map<b>(f: (_: a) => b): Recoverable<b> {
    return ok(f(this.ok));
  }
  ap<b>(f: Recoverable<(_: a) => b>): Recoverable<b> {
    return f.map(g => g(this.ok));
  }
  bind<b>(f: (_: a) => Recoverable<b>): Recoverable<b> {
    return f(this.ok);
  }
  fold1<b>(f: (a: a) => b): b {
    return f(this.ok);
  }
  foldr<b>(f: (z: b, a: a) => b, z: b): b {
    return f(z, this.ok);
  }
  foldl<b>(f: (a: a, z: b) => b, z: b): b {
    return f(this.ok, z);
  }
  recover<b>(recovered: (a: a) => b, ok: (a: a) => b): b {
    return ok(this.ok);
  }
}

export const recovered = <a>(aa: a): Recoverable<a> => new Recovered(aa);
export const ok = <a>(aa: a): Recoverable<a> => new Ok(aa);
export const pure = ok;

export const recover = <a, b>(
  r: Recoverable<a>,
  recovered: (_: a) => b,
  ok: (_: a) => b
): b => r.recover(recovered, ok);

export const ap = <a, b>(
  f: Recoverable<(_: a) => b>,
  aa: Recoverable<a>
): Recoverable<b> => aa.ap(f);

export const join = <a>(aa: Recoverable<Recoverable<a>>): Recoverable<a> =>
  aa.bind(b => b);

export const bind = <a, b>(
  aa: Recoverable<a>,
  f: (a: a) => Recoverable<b>
): Recoverable<b> => aa.bind(f);

export const fold1 = <a, b>(aa: Recoverable<a>, f: (a: a) => b): b => aa.fold1(f);

export const foldr = <a, b>(f: (a: a, b: b) => b, bb: b, aa: Recoverable<a>): b =>
  aa.recover(
    a => f(a, bb),
    a => f(a, bb)
  );

export const liftA2 = <a, b, c>(
  f: (a: a, b: b) => c,
  ra: Recoverable<a>,
  rb: Recoverable<b>
): Recoverable<c> => rb.ap(ra.map(a => b => f(a, b)));

// Traversables
export const traverseNull = <a, b>(
  f: (_: a) => Recoverable<b>,
  m: a | null
): Recoverable<b | null> => (m == null ? pure(null) : f(m));
export const sequenceNull = <a>(
  m: Recoverable<a> | null
): Recoverable<a | null> => traverseNull(a => a, m);

export const traverseArray = <a, b>(
  f: (_: a) => Recoverable<b>,
  l: Array<a>
): Recoverable<Array<b>> =>
  l.reduceRight(
    (b, a) => liftA2((a, b) => b.concat([a]), f(a), b),
    pure<Array<b>>([])
  );
export const sequenceArray = <a>(
  l: Array<Recoverable<a>>
): Recoverable<Array<a>> => traverseArray(a => a, l);

export const traverseNonEmpty = <a, b>(
  f: (_: a) => Recoverable<b>,
  l: NonEmpty<a>
): Recoverable<NonEmpty<b>> =>
  l.tail.reduceRight(
    (z, a) => liftA2((a, b) => NonEmpty.pure(a).concat(b), f(a), z),
    f(l.head).map(NonEmpty.pure)
  );

export const sequenceNonEmpty = <a>(
  l: NonEmpty<Recoverable<a>>
): Recoverable<NonEmpty<a>> => traverseNonEmpty(a => a, l);

// Helper types

export type SSa2Wrapper<a> = { failedHa: number; data: a };
export const serialiseSa2Wrapper = <a>(s: Sa2Wrapper<a>): SSa2Wrapper<a> => ({
  failedHa: s.failedHa,
  data: s.data
});
export const deserialiseSa2Wrapper = <a>(s: SSa2Wrapper<a>): Sa2Wrapper<a> =>
  sa2Wrapper(s.failedHa, s.data);

interface ISa2Wrapper<a> {
  // Functor
  map: <b>(f: (_: a) => b) => Sa2Wrapper<b>;
  // Semigroup
  append: (f: (a: a, b: a) => a, a: Sa2Wrapper<a>) => Sa2Wrapper<a>;
  // Applicative
  ap: <b>(f: Sa2Wrapper<(_: a) => b>) => Sa2Wrapper<b>;
  // Monad
  bind: <b>(f: (_: a) => Sa2Wrapper<b>) => Sa2Wrapper<b>;
}
export class Sa2Wrapper<a> implements ISa2Wrapper<a> {
  public readonly failedHa: number;
  public readonly data: a;
  constructor(failedHa: number, data: a) {
    this.failedHa = failedHa;
    this.data = data;
  }
  map<b>(f: (_: a) => b): Sa2Wrapper<b> {
    return sa2Wrapper(this.failedHa, f(this.data));
  }
  append(f: (a: a, b: a) => a, a: Sa2Wrapper<a>): Sa2Wrapper<a> {
    return sa2Wrapper(this.failedHa + a.failedHa, f(this.data, a.data));
  }
  ap<b>(f: Sa2Wrapper<(_: a) => b>): Sa2Wrapper<b> {
    return sa2Wrapper(this.failedHa + f.failedHa, f.data(this.data));
  }
  bind<b>(f: (_: a) => Sa2Wrapper<b>): Sa2Wrapper<b> {
    const a = f(this.data);
    return sa2Wrapper(this.failedHa + a.failedHa, a.data);
  }
  public static isSa2Wrapper<a>(aa: any): aa is Sa2Wrapper<a> {
    return aa instanceof Sa2Wrapper;
  }
}

export const isSSa2Wrapper = <a>(aa: any): aa is SSa2Wrapper<a> =>
  aa.failedHa != null && aa.data != null;
export const sa2Wrapper = <a>(failedHa: number, data: a): Sa2Wrapper<a> =>
  new Sa2Wrapper(failedHa, data);
